import React, { useState, useEffect, FC, useMemo, useRef } from 'react';
import './forecast-table.scss';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { InputText } from 'primereact/inputtext';
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import ForecastTableRow from '../forecast-table-row/forecast-table-row';

// Add these types at the top
type SortField = string;  // Dynamic based on headers
type SortOrder = 'asc' | 'desc';

interface ForecastTableProps{
    objectList:any[],
    headers:any[],
    setPopupOpen?:Function,
    search: string,
    setSearch: Function,
    showFilters: boolean,
    setShowFilters: Function,
    selectedFilters: string[],
    setSelectedFilters: Function
}
const ForecastTable:FC<ForecastTableProps> = ({objectList, headers, setPopupOpen, search, setSearch, showFilters, setShowFilters, selectedFilters, setSelectedFilters}) => {
    const [stocks, setStocks] = useState<any[]>(objectList)
    const [checkedStocks, setCheckedStocks] = useState([]);
    const [sortField, setSortField] = useState<SortField>('name');
    const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
    const filterContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setStocks(objectList)
    }, [objectList])
    const filteredStock = useMemo(() => {
        const filtered = stocks.filter(stock => {
            const matchesSearch = stock.name.toLowerCase().includes(search.toLowerCase());
            const matchesFilters = selectedFilters.length === 0 || selectedFilters.includes(stock.name);
            return matchesSearch && matchesFilters;
        });

        return [...filtered].sort((a, b) => {
            let comparison = 0;
            const value1 = a[sortField];
            const value2 = b[sortField];

            if (sortField === 'total_percentage') {
                // Remove % sign and convert to float
                const v1 = parseFloat(String(value1).replace('%', ''));
                const v2 = parseFloat(String(value2).replace('%', ''));
                comparison = v1 - v2;
            } else if (typeof value1 === 'number' && typeof value2 === 'number') {
                comparison = value1 - value2;
            } else {
                comparison = String(value1).localeCompare(String(value2));
            }
            
            return sortOrder === 'asc' ? comparison : -comparison;
        });
    }, [stocks, search, sortField, sortOrder, selectedFilters]);

    const allChecked = checkedStocks.length === filteredStock.length;
    
    const setAllChecked = (checked: boolean) => {
        if(checked)
            setCheckedStocks(filteredStock.map(stock => stock.id));
        else
            setCheckedStocks([]);
    }
    const setChecked = (checked: boolean, id: string) => {
        if(checked)
            setCheckedStocks([...checkedStocks, id]);
        else
            setCheckedStocks(checkedStocks.filter(stockId => stockId !== id));
    }

    // Add sort handler
    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortOrder('asc');
        }
    };

    const handleExportToCSV = () => {
        const csvContent = [
            headers.map(h => h.header).join(','),
            ...filteredStock.map(stock => 
                headers.map(h => stock[h.field]).join(',')
            )
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'forecast-data.csv';
        a.click();
        window.URL.revokeObjectURL(url);
    };

    // Add filter handlers
    const handleFilterToggle = (productName: string) => {
        setSelectedFilters(prev => 
            prev.includes(productName) 
                ? prev.filter(name => name !== productName)
                : [...prev, productName]
        );
    };

    const handleSelectAllFilters = (checked: boolean) => {
        setSelectedFilters(checked ? uniqueProductNames : []);
    };

    const uniqueProductNames = useMemo(() => {
        return Array.from(new Set(stocks.map(stock => stock.name)));
    }, [stocks]);

    return (
        <div className="forecast-table">
            <div className="forecast-table__upper">
                <span>Edit columns</span>
                <div className="forecast-table__upper__controls">
                    <IconField iconPosition="left">
                        <InputIcon className="pi pi-search"> </InputIcon>
                        <InputText 
                            placeholder="Search..." 
                            className="forecast-table__upper__controls__filter-button" 
                            onChange={(e) => setSearch(e.target.value)}
                            value={search}
                        />
                    </IconField>
                    <div className="forecast-table__filter-container">
                        <Button 
                            onClick={() => setShowFilters(!showFilters)} 
                            icon="pi pi-filter" 
                            label={'Filters'} 
                            className={`forecast-table__upper__controls__filter-button ${showFilters ? 'p-button-highlighted' : ''} ${selectedFilters.length > 0 ? 'filters-active' : ''}`}
                        />
                        {showFilters && (
                            <div className="forecast-table__filters-popup" ref={filterContainerRef}>
                                <div className="forecast-table__filters-popup__item">
                                    <Checkbox 
                                        checked={selectedFilters.length === uniqueProductNames.length}
                                        onChange={(e) => handleSelectAllFilters(e.checked)}
                                        id="filter-select-all"
                                    />
                                    <label htmlFor="filter-select-all">Select All</label>
                                </div>
                                {uniqueProductNames.map(name => (
                                    <div key={name} className="forecast-table__filters-popup__item">
                                        <Checkbox 
                                            checked={selectedFilters.includes(name)}
                                            onChange={() => handleFilterToggle(name)}
                                            id={`filter-${name}`}
                                        />
                                        <label htmlFor={`filter-${name}`}>{name}</label>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <Button onClick={() => handleExportToCSV()} icon="pi pi-download" iconPos='left' label={'Export'} className="forecast-table__upper__controls__filter-button" />
                </div>
           </div>
            <div className="forecast-table__table">
                <div className="forecast-table__table__header">
                    <Checkbox onChange={e => setAllChecked(e.checked)} checked={allChecked}></Checkbox>
                    {headers.map(header => (
                        <div 
                            key={header.field} 
                            className={`forecast-table__table__header__cell ${header.style} sortable-header`}
                            onClick={() => handleSort(header.field)}
                        >
                            {header.header}
                            {sortField === header.field && 
                                <i className={`pi pi-sort-${sortOrder === 'asc' ? 'up' : 'down'}`}></i>
                            }
                        </div>
                    ))}
                </div>
                <div className="forecast-table__table__body">
                {filteredStock.map((stock) => (
                    <ForecastTableRow key={stock.id} object={stock} headers={headers} checked={checkedStocks.includes(stock.id)} setChecked={(checked, id)=>setChecked(checked, id)}/>
                ))}
                </div>
            </div>

        </div>
    );
}
         
export default ForecastTable;